import { BreakPoint, getDevice } from '@adornis/base/client/frontend-helpers/window-size.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/popover/x-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { CampusLessonDiagnostic } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { type CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-progress-bar.js';
import Cookies from 'js-cookie';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import graduateHat from '../../assets/icons/graduate_hat_filled.svg';
import play from '../../assets/icons/play.svg';
import { CampusRoutes } from '../../routes.js';

@customElement('campus-component-lesson-card')
export class CampusComponentLessonCard extends ChemistryLitElement {
  @property({ attribute: false }) public mode: 'play' | 'edit' | 'custom' | 'progress' | 'progress_employee' = 'play';
  @property({ type: Boolean }) condensed = false;
  @property({ attribute: false }) public marked = new RXController<boolean>(this, false);

  @property({ attribute: false }) public section = new RXController<Maybe<CampusSection>>(this, null);

  @state() private readonly lesson = new RXController<Maybe<CampusLesson>>(this, null);

  @property({ attribute: false }) public diagnosticsForLessons = new RXController<CampusLessonDiagnostic[]>(this, []);

  public get isMobile(): boolean {
    return getDevice() <= BreakPoint.phone;
  }

  public get isTablet(): boolean {
    return getDevice() <= BreakPoint.tablet;
  }

  override render() {
    if (!this.lesson.value)
      return html`
        <cr-flex horizontal crossaxis-center space-between ${css({ background: 'white' })}>
          <cr-text>...</cr-text>
        </cr-flex>
      `;

    if (this.condensed) return this._getCondensedView();

    return html`
      <cr-flex
        horizontal
        crossaxis-center
        space-between
        ${css({ background: 'white' })}
        class="
          ${!!this._checkLessonIsPlayable() && this.mode === 'play' ? 'locked' : ''}
          ${this.mode === 'edit' ? 'draggable' : ''}
        "
      >
        <cr-flex w100 horizontal space="md" crossaxis-center wrap end>
          <cr-flex horizontal flex space="sm" ${css({ alignItems: 'start' })}>
            ${this.mode === 'edit' && !this.isMobile
              ? html`<cr-icon
                  ${css({ fontSize: '2rem', cursor: 'grab', userSelect: 'none', color: 'rgba(0, 0, 0, 0.4)' })}
                  class="handle"
                >
                  drag_indicator
                </cr-icon>`
              : nothing}
            <img
              src=${graduateHat}
              ${css({ width: '40px', zIndex: '0', userSelect: 'none', userDrag: 'none', selfAlign: 'top' })}
            />
            <cr-flex space="sm">
              <cr-flex space="sm" horizontal crossaxis-center>
                <cr-text bold ${css({ overflowWrap: 'normal' })}> ${this.lesson.value.name} </cr-text>
                ${this.lesson.value.hiddenFromStudents
                  ? html` <cr-icon ${css({ fontSize: '1.2rem', color: '#adadad' })}>visibility_off</cr-icon> `
                  : nothing}
              </cr-flex>

              <cr-text>${this.lesson.value.exerciseIDs.length} Aufgabe(n) </cr-text>
            </cr-flex>
            ${this._getHiddenWarning()}
            <cr-flex></cr-flex>
          </cr-flex>
          <cr-flex
            horizontal
            space="md"
            ${css({
              justifyContent: 'end',
              flexDirection: this.isMobile ? 'column' : 'row',
            })}
          >
            ${this._getLessonControls()}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getCondensedView() {
    return html`
      <cr-flex
        horizontal
        crossaxis-center
        space-between
        ${css({ background: 'white' })}
        class="
          ${!!this._checkLessonIsPlayable() && this.mode === 'play' ? 'locked' : ''}
          ${this.mode === 'edit' ? 'draggable' : ''}
        "
      >
        <cr-flex w100 horizontal space="md" crossaxis-center wrap end>
          <cr-flex horizontal flex space="sm" ${css({ alignItems: 'start' })} crossaxis-center>
            ${this.mode === 'edit' && !this.isMobile
              ? html`<cr-icon
                  ${css({ fontSize: '2rem', cursor: 'pointer', userSelect: 'none', color: 'rgba(0, 0, 0, 0.4)' })}
                  class="handle"
                >
                  drag_indicator
                </cr-icon>`
              : nothing}
            <img
              src=${graduateHat}
              ${css({ width: '20px', zIndex: '0', userSelect: 'none', userDrag: 'none', selfAlign: 'top' })}
            />

            <cr-flex space="sm" horizontal crossaxis-center>
              <cr-text bold ${css({ overflowWrap: 'normal' })}> ${this.lesson.value?.name} </cr-text>
              ${this.lesson.value?.hiddenFromStudents
                ? html` <cr-icon ${css({ fontSize: '1.2rem', color: '#adadad' })}>visibility_off</cr-icon> `
                : nothing}
              <cr-text>${this.lesson.value?.exerciseIDs.length} Aufgabe(n) </cr-text>
            </cr-flex>

            ${this._getHiddenWarning()}
            <cr-flex></cr-flex>
          </cr-flex>
          <cr-flex
            horizontal
            space="md"
            ${css({
              justifyContent: 'end',
              flexDirection: this.isMobile ? 'column' : 'row',
            })}
          >
            ${this._getLessonControls()}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getHiddenWarning() {
    return html`
      ${this.marked.value && (this.lesson.value?.hiddenFromStudents || this.section.value?.hiddenFromStudents)
        ? html`
            <cr-icon ${css({ fontSize: '1.6rem', color: this.colors.secondary, cursor: 'pointer' })}>
              warning
              <x-tooltip>
                <cr-text>
                  ${this.section.value?.hiddenFromStudents ? 'Themenbereich ist versteckt' : 'Lektion ist versteckt'}
                  Die Aufgabe wird den Schüler:innen nicht angezeigt
                </cr-text>
              </x-tooltip>
            </cr-icon>
          `
        : nothing}
    `;
  }

  private _getLessonControls() {
    switch (this.mode) {
      case 'edit':
        return html`
          <cr-flex horizontal space="md" crossaxis-center>

            ${(() => {
              if (this.section.value.area === CampusArea.EXTERNAL_EMPLOYEES) {
                return permissionGuard(
                  ChancenPermission.campus.EDIT_CONTENT_EXTERNAL,
                  () => html`<cr-button
                    ${css({ maxWidth: this.isMobile ? '40%' : 'unset' })}
                    href=${CampusRoutes.lessons + '/edit/' + this.lesson.value?._id + '?section=' + this.section.value?._id}
                  >
                    Bearbeiten
                  </cr-button>`,
                  {
                    context: ANY_CONTEXT,
                  },
                );
              }

              return permissionGuard(
                ChancenPermission.campus.EDIT_CONTENT,
                () => html`<cr-button
                  ${css({ maxWidth: this.isMobile ? '40%' : 'unset' })}
                  href=${CampusRoutes.lessons + '/edit/' + this.lesson.value?._id + '?section=' + this.section.value?._id}
                >
                  Bearbeiten
                </cr-button>`,
                {
                  context: ANY_CONTEXT,
                },
              );
            })()}

            <cr-button
              mode="fab"
              over-animation="jump"
              icon=${play}
              @click=${() => {
                Cookies.set('internalRouting', 'true');
                goTo(CampusRoutes.play + '/' + this.lesson.value?._id);
              }}
            >
            </cr-button>
          </cr-flex>
        `;
      case 'play':
        return html`
          <cr-flex horizontal space="md" crossaxis-center ${css({ width: '100%' })}>
            <cr-progress-bar
              ${css({ width: '100%' })}
              .value=${this.diagnosticsForLessons.value.find(
                diagnostic => diagnostic.lessonID === this.lesson.value?._id,
              )?.ratio ?? 0}
            ></cr-progress-bar>
            <cr-button
              mode="fab"
              over-animation="jump"
              ?small=${this.condensed}
              icon=${play}
              @click=${async () => {
                const violatedDependency = this._checkLessonIsPlayable();
                if (violatedDependency) {
                  await ChromiumDialog.alert(
                    'Du kannst dieses Quiz noch nicht betreten, dir fehlt folgende Voraussetzung: ' +
                      violatedDependency,
                  );
                  return;
                }
                Cookies.set('internalRouting', 'true');
                goTo(CampusRoutes.play + '/' + this.lesson.value?._id);
              }}
            >
            </cr-button>
          </cr-flex>
        `;
      case 'custom':
        return html`
          <cr-button
            mode="fab"
            icon=${this.marked.value ? 'check' : 'add'}
            tone="${this.marked.value ? 'checked' : ''}"
            @click=${async () => {
              const event = new CustomEvent('lessonClicked', {
                detail: {
                  lesson: this.lesson.value,
                  section: this.section.value,
                },
              });
              this.dispatchEvent(event);
            }}
          ></cr-button>
        `;
      case 'progress_employee': {
        const diagnostic = this.diagnosticsForLessons.value.find(
          diagnostic => diagnostic.lessonID === this.lesson.value?._id,
        );
        return html`
          <cr-grid columns="max-content" space="sm">
            <cr-text>Fortschritt:</cr-text>
            <cr-progress-bar ${css({ width: '100%' })} .value=${diagnostic?.ratio ?? 0}></cr-progress-bar>
          </cr-grid>
        `;
      }
      case 'progress':
        const diagnostic = this.diagnosticsForLessons.value.find(
          diagnostic => diagnostic.lessonID === this.lesson.value?._id,
        );
        return html`
          <cr-grid columns="140px max-content" space="sm">
            <cr-text
              >Korrekt gelöst: ${diagnostic?.correctExercices} / ${diagnostic?.scoreableExercices}
              <cr-tooltip>
                <cr-text>
                  ${!diagnostic?.partialScores?.length
                    ? 'Keine teilweise korrekten Lösungen gefunden'
                    : `${new Array(diagnostic?.scoreableExercices ?? 0)
                        .fill(null)
                        .map((_, i) => {
                          const score = diagnostic?.partialScores?.[i] ?? 0;
                          return (score * 100).toFixed(0) + '%';
                        })
                        .join(', ')}`}
                </cr-text>
              </cr-tooltip>
            </cr-text>
            <cr-text>Fortschritt:</cr-text>
            <cr-progress-bar ${css({ width: '100%' })} .value=${diagnostic?.correctRatio ?? 0}></cr-progress-bar>
            <cr-progress-bar ${css({ width: '100%' })} .value=${diagnostic?.ratio ?? 0}></cr-progress-bar>
          </cr-grid>
        `;

      default:
        break;
    }
  }

  private _checkLessonIsPlayable(): string | undefined {
    const diagnosticForLesson = this.diagnosticsForLessons.value.find(dia => dia.lessonID === this.lesson.value?._id);
    if (!diagnosticForLesson) return undefined;
    for (let i = 0; i < diagnosticForLesson.dependingOnLesson.length; i++) {
      if ((diagnosticForLesson.dependingOnLesson[i]?.ratio ?? 0) < 0.5)
        return diagnosticForLesson.dependingOnLesson[i]?.name;
    }
    return undefined;
  }
}
